import { useEffect, useRef } from 'react';

/**
 * A hook for syncing data with WebSocket updates
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.websocket - WebSocket connection
 * @param {number} options.messageId - The message ID to listen for
 * @param {Function} options.fetchLatestData - Function to fetch the latest data
 * @param {Function} options.processUpdates - Function to process updates
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @param {Array} options.dependencies - Additional dependencies for the effect
 */
const useWebSocketDataSync = ({
  websocket,
  messageId,
  fetchLatestData,
  processUpdates,
  onSuccess,
  onError,
  dependencies = []
}) => {
  const processingRef = useRef(false);
  const lastProcessedTimeRef = useRef(0);
  
  useEffect(() => {
    if (!websocket) return;
    
    const handleWebSocketMessage = async (message) => {
      try {
        const data = JSON.parse(message.data);
        
        // Check if this is the message we're interested in
        if (data.messageId === messageId) {
          console.log(`Received WebSocket message with ID ${messageId}`);
          
          // Prevent concurrent processing and throttle frequent updates
          if (processingRef.current) {
            console.log('Already processing an update, skipping');
            return;
          }
          
          // Throttle updates to prevent rapid consecutive calls
          const now = Date.now();
          if (now - lastProcessedTimeRef.current < 2000) {
            console.log('Update throttled (too frequent), skipping');
            return;
          }
          
          processingRef.current = true;
          lastProcessedTimeRef.current = now;
          
          try {
            // Fetch the latest data
            const latestData = await fetchLatestData();
            
            // Process the updates
            const changes = await processUpdates(latestData);
            
            // If changes were made, call the success callback
            if (changes && changes.length > 0) {
              if (onSuccess) onSuccess(changes);
            }
          } catch (error) {
            console.error('Error processing data updates:', error);
            if (onError) onError(error);
          } finally {
            processingRef.current = false;
          }
        }
      } catch (error) {
        console.error('Error processing websocket message:', error);
      }
    };
    
    websocket.addEventListener('message', handleWebSocketMessage);
    
    return () => {
      websocket.removeEventListener('message', handleWebSocketMessage);
    };
  }, [websocket, messageId, fetchLatestData, processUpdates, onSuccess, onError, ...dependencies]);
};

export default useWebSocketDataSync; 